/**
 * @file
 * Custom DOM manipulation or DOM traversal functions.
 */

/**
 * Find the nearest preceding sibling that is not an empty text node.
 *
 * @param node
 *   The starting point.
 *
 * @return
 *   The last non-empty text node before the starting point.
 */
export function getPreviousSiblingIgnoringEmptyTextNodes(
  node: Node,
): Node | null {
  const previous = node.previousSibling;
  if (!previous) {
    // There are no previous siblings.
    return null;
  }

  if (previous.nodeType !== 3 || previous.nodeValue !== "") {
    // This is the node we want.
    return previous;
  }

  // This is an empty text node. We need to go back some more.
  return getPreviousSiblingIgnoringEmptyTextNodes(previous);
}

/**
 * Check if the given paragraph element is considered empty for the purpose of removing empty paragraphs from markup.
 *
 * @param el
 *   The paragraph element to check.
 */
export function isEmptyParagraph(el: Element): boolean {
  return (
    [...el.childNodes].findIndex((node: Node) => {
      switch (node.nodeType) {
        case node.ELEMENT_NODE:
          switch ((node as Element).tagName) {
            case "BR":
              // Line breaks don't count as content.
              return false;
          }
          break;

        case node.TEXT_NODE:
          // Text only counts as content if it's more than just whitespace.
          return (node.nodeValue || "").trim().length > 0;
      }

      // Everything else counts as content, so the paragraph is not empty.
      return true;
    }) === -1
  );
}

/**
 * Add style heading class name on heading to keep style after
 * @param body
 *   The body DOM.
 */
export function addHeadingClassCorrespondingLevels(body: HTMLElement): void {
  body.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading) => {
    const level = parseInt(heading.nodeName.charAt(1));
    heading.classList.add("article-title", `article-title--${level}`);
  });
}

/**
 * Change heading levels.
 *
 * @param window
 *   The window object. In a browser, just use `window`. In Node, use something like JSDOM.
 * @param body
 *   The body DOM.
 * @param offset
 *   By how many levels to change each heading.
 *   - `1` means: Change `h1` to `h2` (demote headings).
 *   - `-1` means: Change `h2` to `h1` (promote headings).
 */
export function shiftHeadingLevels(
  window: Window & typeof globalThis,
  body: HTMLElement,
  offset: number,
): void {
  body.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading) => {
    const level = parseInt(heading.nodeName.charAt(1));
    const newLevel = level + offset;
    if (newLevel >= 1 && newLevel <= 6) {
      changeElementTagName(window, heading, `h${newLevel}`);
    }
  });
}

/**
 * Change the tag name of an HTML element by creating a new one with identical children and attributes, but with a
 * different tag name.
 *
 * @param window
 *   The window object. In a browser, just use `window`. In Node, use something like JSDOM.
 * @param el
 *   The element to change.
 * @param newTagName
 *   The new tag name.
 */
export function changeElementTagName(
  window: Window,
  el: Element,
  newTagName: string,
): void {
  const newElement = window.document.createElement(newTagName);

  // Move child nodes.
  [...el.childNodes].forEach((node: ChildNode) => {
    newElement.appendChild(node);
  });

  // Copy attributes.
  [...el.attributes].forEach((attr) => {
    newElement.setAttribute(attr.name, attr.value);
  });

  // Replace the old element with the new one.
  el.replaceWith(newElement);
}

/**
 * Wrap the children of an element in a new element.
 *
 * @param window
 *   The window object. In a browser, just use `window`. In Node, use something like JSDOM.
 * @param el
 *   The element whose children must be wrapped. Afterward, this element will have only one child, the new element.
 *   The new element will contain the original children.
 * @param newTagNameOrWrapper
 *   The tag name of the new element or the already created wrapper HTMLElement.
 */
export function wrapChildren(
  window: Window,
  el: Element,
  newTagNameOrWrapper: string | HTMLElement,
): void {
  const wrapper =
    typeof newTagNameOrWrapper === "string"
      ? window.document.createElement(newTagNameOrWrapper)
      : newTagNameOrWrapper;

  // Move child nodes.
  [...el.childNodes].forEach((node: ChildNode) => {
    wrapper.appendChild(node);
  });

  // Replace the children of the old element with the new element.
  el.appendChild(wrapper);
}

/**
 * Wrap an element in a new element.
 *
 * @param window
 *   The window object. In a browser, just use `window`. In Node, use something like JSDOM.
 * @param el
 *   The element whose must be wrapped. Should have a parentNode
 * @param newTagNameOrWrapper
 *   The tag name of the new element or the already created wrapper HTMLElement.
 */
export function wrapElement(
  window: Window,
  el: Element,
  newTagNameOrWrapper: string | HTMLElement,
): void {
  const wrapper =
    typeof newTagNameOrWrapper === "string"
      ? window.document.createElement(newTagNameOrWrapper)
      : newTagNameOrWrapper;
  const parentNode = el.parentNode;
  if (parentNode) {
    parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  }
}
